import { useAuth, useUser } from '@clerk/clerk-react';
import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/api';

export const Lobby = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { getToken } = useAuth();
  
  //ROLE STATE: check for instructor else default to student
  const role = (user?.publicMetadata?.role as string) || 'student';
  const isInstructor = role === 'instructor';
  
  // Join Session State
  const [sessionCode, setSessionCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  
  // Create Session State
  const [isCreating, setIsCreating] = useState(false);
  
  const [error, setError] = useState<string | null>(null);
  const [devicesGranted, setDevicesGranted] = useState(false);

  const handleDeviceAccess = async () => {
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      setDevicesGranted(true);
    } catch (err) {
      console.warn('Webcam access denied', err);
      setDevicesGranted(false);
    }
  };

  // LOGIC: Join an existing session (Student)
  const handleJoinSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!sessionCode) return;

    setIsJoining(true);
    setError(null);
    try {
      const token = await getToken();
      
      // Sanitization: Ensure code is clean before sending
      const cleanCode = sessionCode.trim().toUpperCase();

      const { data } = await apiClient.post(
        `/sessions/${cleanCode}/join`,
        { role: 'student' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      navigate(`/session/${data.sessionId}/student`, { state: data });
    } catch (err: any) {
      console.error(err);
      setError('Unable to join. Please check the code.');
    } finally {
      setIsJoining(false);
    }
  };

  // LOGIC: Create a new session (Instructor)
  const handleCreateSession = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const token = await getToken();
      // 1. Call Backend to create session (Backend now generates the Short Code)
      const { data } = await apiClient.post(
        '/sessions',
        {}, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // 2. Redirect to Instructor Dashboard
      // data.sessionId will be the new format (e.g., "XC92KL")
      navigate(`/session/${data.sessionId}/instructor`, { state: data });
    } catch (err: any) {
      console.error(err);
      setError('Failed to create session. Ensure you are logged in.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl space-y-8 p-4">
      
      {/* 1. HEADER & ROLE TOGGLE */}
      <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
        <div>
          <p className="text-sm text-text-dimmed">Signed in as</p>
          <p className="text-xl font-semibold text-text-primary">{user?.fullName}</p>
        </div>
        <div className={`px-3 py-1 rounded-full text-xs font-bold border ${isInstructor ? 'bg-brand/20 border-brand text-brand' : 'bg-neon-red/20 border-neon-red text-neon-red'}`}>
            {isInstructor ? 'INSTRUCTOR' : 'STUDENT'}
        </div>
      </div>

      {/* 2. STUDENT VIEW: Join Form */}
      {!isInstructor && (
        <div className="rounded-2xl border border-background-border bg-background-surface p-8 shadow-2xl shadow-glow animate-in fade-in zoom-in duration-300">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-text-primary">Join a Classroom</h2>
            <p className="text-text-dimmed">Enter the code provided by your instructor.</p>
          </div>

          <form onSubmit={handleJoinSubmit} className="space-y-6">
            <label className="block text-sm font-medium text-text-primary">
              Class Code
              <input
                className="mt-2 w-full rounded-lg border border-background-border bg-background-main/60 px-4 py-3 text-text-primary outline-none focus:border-brand focus:shadow-glow font-mono tracking-widest uppercase placeholder:normal-case placeholder:tracking-normal"
                value={sessionCode}
                onChange={(event) => {
                  const val = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                  setSessionCode(val);
                }}
                placeholder="e.g. XC92KL"
                maxLength={6}
              />
            </label>

            {/* Readiness Checklist */}
            <div className="rounded-lg bg-background-main/50 p-4 text-sm text-text-dimmed border border-background-border">
              <p className="font-medium text-text-primary">Readiness checklist</p>
              <ul className="mt-3 space-y-2">
                <li className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${devicesGranted ? 'bg-green-400' : 'bg-text-dimmed'}`} />
                  Webcam + microphone access
                  <button
                    type="button"
                    onClick={handleDeviceAccess}
                    className="ml-auto rounded border border-background-border px-3 py-1 text-xs hover:border-brand hover:text-text-primary transition-colors"
                  >
                    Test
                  </button>
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-green-400" />
                  Emotion data is processed locally.
                </li>
              </ul>
            </div>

            {error && <p className="text-sm text-neon-red">{error}</p>}

            <button
              type="submit"
              disabled={isJoining || isCreating || sessionCode.length < 3}
              className="w-full rounded-lg bg-neon-gradient px-4 py-3 font-semibold text-white hover:shadow-glow disabled:opacity-60 transition-all"
            >
              {isJoining ? 'Joining session...' : 'Enter Classroom'}
            </button>
          </form>
        </div>
      )}

      {/* 3. INSTRUCTOR VIEW: Create Dashboard */}
      {isInstructor && (
        <div className="rounded-2xl border border-background-border bg-background-surface p-8 shadow-2xl shadow-glow animate-in fade-in zoom-in duration-300">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-text-primary">Instructor Dashboard</h2>
            <p className="text-text-dimmed">Launch a new session and monitor real-time engagement.</p>
          </div>

          <div className="space-y-4">
             <div className="p-4 bg-brand/10 border border-brand/20 rounded-xl">
                <p className="text-sm text-brand mb-2 font-semibold">Session Capabilities</p>
                <ul className="text-xs text-text-dimmed space-y-1 list-disc pl-4">
                   <li>Real-time Emotion Analysis (TensorFlow.js)</li>
                   <li>Live Engagement Heatmap</li>
                   <li>Post-session Analytics Report</li>
                </ul>
             </div>

             <button
              type="button"
              onClick={handleCreateSession}
              disabled={isJoining || isCreating}
              className="w-full rounded-lg bg-neon-gradient px-4 py-4 font-bold text-white hover:shadow-glow disabled:opacity-60 transition-all"
            >
              {isCreating ? 'Provisioning Room...' : 'Start New Class'}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};